import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@/components/ui/Icon";
import { useSession } from "@/lib/auth/useSession";
import { navItemByPath, visibleNavItems, type NavItem } from "@/app/navigation";
import {
  navTree,
  placementOf,
  sectionOfPath,
  trailOf,
  NAV_SECTIONS,
  type SectionId,
} from "@/app/navSections";
import { useNavCounts, type NavCount } from "@/app/navCounts";
import {
  applyTheme,
  readTheme,
  resolvedTheme,
  watchSystemTheme,
  type Theme,
} from "@/app/theme";
import { useLiveUpdates } from "@/lib/api/stream";
import { roleLabel } from "@/lib/labels";
import brandMark from "@/assets/brand/uyda-mark.svg";
import brandLogo from "@/assets/brand/uyda-logo.svg";

const COLLAPSE_KEY = "uyda.sidebar.collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Qobiq — uch qatlam:
 *
 *   1. RELS (chap chetda, tor va qora) — bo'limlar. Bir qarashda
 *      «qayerdaman» degan savolga javob beradi.
 *   2. YON MENYU (och kulrang) — tanlangan bo'limning guruhlari,
 *      modullari va ularning bolalari daraxt chizig'i bilan. Tepada
 *      foydalanuvchi va qidiruv.
 *   3. VARAQ (oq) — sahifaning o'zi.
 *
 * Avvalgi yagona uzun ro'yxat (27 modul) o'rniga daraxt: xodim
 * avval bo'limni, keyin modulni tanlaydi — ko'z bir vaqtda 5–8
 * qatorni ko'radi, 27 tani emas.
 */
export function AppShell() {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [theme, setTheme] = useState<Theme>(readTheme);
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  // Tor ekranda (planshet) yon menyu USTIGA ochiladi va modul
  // tanlangach yopiladi.
  const [navOpen, setNavOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  // Oqim BUTUN qobiq uchun bitta: har bir modul o'zi ulansa,
  // o'n uchta parallel ulanish ochilardi.
  const live = useLiveUpdates();

  const permissions = useMemo(() => session.user?.permissions ?? [], [session.user]);
  const tree = useMemo(() => navTree(permissions), [permissions]);
  const counts = useNavCounts(permissions);
  const current = navItemByPath(location.pathname);
  const currentSection = sectionOfPath(location.pathname)?.id ?? tree[0]?.id ?? "home";
  const [sectionId, setSectionId] = useState<SectionId>(currentSection);
  const mode = resolvedTheme(theme);

  useEffect(() => {
    applyTheme(theme);
    // "system" tanlovida tizim mavzusi o'zgarsa interfeys ham
    // o'zgarishi kerak — ilova qayta ochilishini kutmasdan.
    return watchSystemTheme(theme, () => applyTheme(theme));
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      /* e'tiborsiz */
    }
  }, [collapsed]);

  // Sahifa almashganda menyu O'SHA bo'limni ko'rsatadi: havoladan
  // yoki boshqaruv panelidagi tugmadan kelgan xodim menyuda o'zi
  // turgan joyni ko'rishi kerak.
  useEffect(() => {
    setSectionId(currentSection);
    setNavOpen(false);
    setQuery("");
  }, [location.pathname, currentSection]);

  // Ctrl/⌘+K — menyudan qidirish. Yigirmadan ortiq modulda
  // sichqoncha bilan qidirishdan tezroq.
  useEffect(() => {
    function onKey(event: globalThis.KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCollapsed(false);
        setNavOpen(true);
        requestAnimationFrame(() => searchRef.current?.focus());
      } else if (event.key === "Escape") {
        setNavOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const section = tree.find((entry) => entry.id === sectionId) ?? tree[0];
  // «Asosiy» bo'limida — butun tizim bo'yicha DIQQAT talab qiladigan
  // modullar (sanog'i bor). Bosh sahifaga kirgan rahbar qayerda ish
  // to'planganini bo'limlarni birma-bir ochmasdan ko'radi.
  const attention = useMemo(
    () =>
      tree
        .flatMap((entry) => entry.items)
        .filter((item) => counts[item.path] && item.path !== "/")
        .sort((a, b) => Number(counts[b.path]?.tone === "error") - Number(counts[a.path]?.tone === "error")),
    [tree, counts],
  );
  const results = useMemo(() => searchItems(visibleNavItems(permissions), query), [permissions, query]);

  function openSection(id: SectionId) {
    if (id === sectionId && (navOpen || !collapsed)) {
      // Tor ekranda shu bo'lim belgisini qayta bosish menyuni yopadi.
      setNavOpen((open) => !open);
      return;
    }
    setSectionId(id);
    setCollapsed(false);
    setNavOpen(true);
  }

  function onSearchKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" && results[0]) {
      navigate(results[0].path);
    } else if (event.key === "Escape") {
      setQuery("");
    }
  }

  const trail = trailOf(current);

  return (
    <div className={`shell${collapsed ? " collapsed" : ""}${navOpen ? " nav-open" : ""}`}>
      <nav className="rail" aria-label="Bo'limlar">
        <div className="rail-brand brand-mark" aria-hidden="true" title="Uyda WMS · Gold Dishes">
          <img src={brandMark} alt="" />
        </div>

        <div className="rail-sections">
          {tree.map((entry) => {
            const alert = entry.items.some((item) => counts[item.path]?.tone === "error");
            const active = entry.id === (section?.id ?? sectionId);
            return (
              <button
                key={entry.id}
                type="button"
                className={`rail-btn${active ? " active" : ""}${
                  entry.id === currentSection ? " current" : ""
                }`}
                onClick={() => openSection(entry.id)}
                aria-label={entry.label}
                aria-pressed={active}
                data-label={entry.label}
              >
                <Icon name={entry.icon} size={20} />
                {alert && <span className="rail-alert" aria-hidden="true" />}
              </button>
            );
          })}
        </div>

        <div className="rail-spacer" />

        <div className="rail-foot">
          <button
            type="button"
            className="rail-btn"
            onClick={() => setTheme(mode === "dark" ? "light" : "dark")}
            aria-label={mode === "dark" ? "Yorug' mavzu" : "Qorong'i mavzu"}
            data-label={mode === "dark" ? "Yorug' mavzu" : "Qorong'i mavzu"}
          >
            <Icon name={mode === "dark" ? "sun" : "moon"} size={20} />
          </button>
          <button
            type="button"
            className="rail-btn rail-collapse"
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? "Menyuni ochish" : "Menyuni yig'ish"}
            data-label={collapsed ? "Menyuni ochish" : "Menyuni yig'ish"}
          >
            <Icon name="sidebar" size={20} />
          </button>
          <button
            type="button"
            className="rail-btn"
            onClick={session.signOut}
            aria-label="Chiqish"
            data-label="Chiqish"
          >
            <Icon name="logout" size={20} />
          </button>
        </div>
      </nav>

      <div className="window">
        <button
          type="button"
          className="nav-scrim"
          aria-label="Menyuni yopish"
          tabIndex={-1}
          onClick={() => setNavOpen(false)}
        />

        <nav className="sidebar" aria-label="Asosiy navigatsiya">
          {session.user && (
            <div className="user-chip">
              <span className="avatar" aria-hidden="true">
                {initials(session.user.fullName || session.user.username)}
              </span>
              <span className="user-details">
                <span className="user-name">
                  {session.user.fullName || session.user.username}
                </span>
                {/* Rol KO'RINIB turadi: vakolat shundan kelib chiqadi
                    va operator nega tugma yo'qligini tushunadi. */}
                <span className="user-role">
                  {session.user.roles.map(roleLabel).join(", ") || "rolsiz"}
                </span>
              </span>
            </div>
          )}

          <label className="side-search search-box">
            <Icon name="search" size={16} className="icon-inside" />
            <input
              ref={searchRef}
              className="input"
              type="search"
              placeholder="Menyudan qidirish"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onSearchKey}
              aria-label="Menyudan qidirish"
            />
            {!query && <kbd className="side-kbd">{SHORTCUT}</kbd>}
          </label>

          <div className="nav-scroll">
            {query.trim() ? (
              <div className="nav-group">
                <div className="nav-group-label">
                  {results.length > 0 ? `Topildi: ${results.length}` : "Hech narsa topilmadi"}
                </div>
                <div className="nav-group-items">
                  {results.map((item) => (
                    <NavRow
                      key={item.path}
                      item={item}
                      count={counts[item.path]}
                      sub={NAV_SECTIONS.find((entry) => entry.id === placementOf(item).section)?.label}
                    />
                  ))}
                </div>
              </div>
            ) : (
              section && (
                <>
                  <div className="nav-section-title">{section.label}</div>
                  {/* Guruh sarlavhasi kichik va kulrang: u bo'limni
                      ajratadi, lekin modul nomlari bilan raqobat
                      qilmaydi. */}
                  {section.tree.map((group) => (
                    <div className="nav-group" key={group.id}>
                      {section.tree.length > 1 || group.label !== section.label ? (
                        <div className="nav-group-label">{group.label}</div>
                      ) : null}
                      <div className="nav-group-items">
                        {group.nodes.map((node) => (
                          <div className="nav-node" key={node.item.path}>
                            <NavRow item={node.item} count={counts[node.item.path]} />
                            {node.children.length > 0 && (
                              <div className="nav-children">
                                {node.children.map((child) => (
                                  <NavRow
                                    key={child.path}
                                    item={child}
                                    count={counts[child.path]}
                                    child
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {section.id === "home" && attention.length > 0 && (
                    <div className="nav-group">
                      <div className="nav-group-label">Diqqat talab qiladi</div>
                      <div className="nav-group-items">
                        {attention.map((item) => (
                          <NavRow
                            key={item.path}
                            item={item}
                            count={counts[item.path]}
                            sub={
                              NAV_SECTIONS.find((entry) => entry.id === placementOf(item).section)
                                ?.label
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )
            )}
          </div>

          <div className="sidebar-foot">
            <img className="brand-logo" src={brandLogo} alt="Uyda" />
            <span className="brand-sub">WMS · Gold Dishes</span>
          </div>
        </nav>

        <div className="main">
          <header className="topbar">
            {/* Yuqori panelda TO'LIQ zanjir: bo'lim › guruh › ota
                modul › sahifa. Daraxtning qaysi shoxida turgani
                sarlavhadan ko'rinmaydi. */}
            <nav className="crumbs" aria-label="Joylashuv">
              {trail.map((part) => (
                <span className="crumb" key={part}>
                  <span className="crumb-group">{part}</span>
                  <Icon name="chevronRight" size={14} className="crumb-sep" />
                </span>
              ))}
              <span className="crumb-current">{current?.title ?? "UYDA WMS"}</span>
            </nav>

            <div className="topbar-spacer" />

            <div className="topbar-actions">
              <span
                className={`live live-${live}`}
                title={LIVE_HINT[live]}
                aria-label={LIVE_HINT[live]}
              >
                <span className="live-dot" />
                <span className="live-text">{LIVE_LABEL[live]}</span>
              </span>
            </div>
          </header>

          <main className="page">
            <div className="page-inner">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

/** Menyu qatori: belgi, nom va o'ngda sanoq tabletkasi. */
function NavRow({
  item,
  count,
  child,
  sub,
}: {
  item: NavItem;
  count?: NavCount;
  child?: boolean;
  sub?: string;
}) {
  return (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      className={({ isActive }) =>
        `nav-item${isActive ? " active" : ""}${child ? " nav-child" : ""}`
      }
      title={item.hint || item.title}
    >
      <span className="nav-icon">
        <Icon name={item.icon} size={child ? 16 : 18} />
      </span>
      <span className="nav-label">
        {item.label}
        {sub && <span className="nav-sub">{sub}</span>}
      </span>
      {count && (
        <span
          className={`nav-count num${count.tone ? ` nav-count-${count.tone}` : ""}`}
          title={count.hint}
          aria-label={count.hint}
        >
          {count.count > 999 ? "999" : count.count}
          {count.more || count.count > 999 ? "+" : ""}
        </span>
      )}
    </NavLink>
  );
}

/** Nomi, sarlavhasi yoki izohi bo'yicha qidiruv. */
function searchItems(items: NavItem[], query: string): NavItem[] {
  const needle = query.trim().toLocaleLowerCase("uz");
  if (!needle) return [];
  const scored = items
    .map((item) => {
      const label = item.label.toLocaleLowerCase("uz");
      const title = item.title.toLocaleLowerCase("uz");
      const hint = item.hint.toLocaleLowerCase("uz");
      const score = label.startsWith(needle)
        ? 0
        : title.startsWith(needle)
          ? 1
          : label.includes(needle) || title.includes(needle)
            ? 2
            : hint.includes(needle)
              ? 3
              : -1;
      return { item, score };
    })
    .filter((entry) => entry.score >= 0);
  return scored.sort((a, b) => a.score - b.score).map((entry) => entry.item);
}

const SHORTCUT =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl K";

const LIVE_LABEL: Record<string, string> = {
  live: "Jonli",
  connecting: "Ulanmoqda",
  offline: "Uzildi",
  mock: "Namuna",
};

const LIVE_HINT: Record<string, string> = {
  live: "O'zgarishlar serverdan darhol keladi",
  connecting: "Yangilanish oqimiga ulanmoqda",
  offline: "Oqim uzildi — ro'yxatlarni qo'lda yangilang",
  mock: "Namuna ma'lumot — serverga ulanmagan",
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}
